/**
 * Der Leerzustand — „hier steht noch nichts, und das ist in Ordnung".
 *
 * Übernommen aus dem `Placeholder` in `ui_kits/dashboard/index.html`, dort ein
 * Hinweis an den Betrachter des Kits. Hier trägt er mehr Gewicht: HausMeter
 * zeigt bis zu den ersten Werten viele leere Flächen, und jede einzelne muss
 * sagen, **warum** sie leer ist. Eine leere Fläche ohne Erklärung sieht aus wie
 * ein Fehler.
 *
 * **Kein Ersatz für eine Zahl.** Wo ein Wert fehlt, steht hier ein Satz — nie
 * eine Null und nie ein Beispielwert. Eine Null ist eine Messung, die niemand
 * vorgenommen hat (Regel 8: Lücken sind Lücken).
 */
import type { ReactNode } from "react";
import { fontWeight } from "../tokens.js";

export interface EmptyStateProps {
  /** Die Überschrift — kurz, sachlich, kein Bedauern. */
  readonly title: ReactNode;
  /** Ein bis zwei Sätze: warum ist es leer, und was ändert das. */
  readonly children?: ReactNode;
  readonly icon?: ReactNode;
  /** Was man hier tun kann — falls es etwas zu tun gibt. */
  readonly action?: ReactNode;
  /**
   * `sm` für das Innere einer Karte, `md` für eine ganze Seite. Der Unterschied
   * ist nur die Höhe: Ein Seiten-Leerzustand darf Luft haben, einer in einer
   * Karte nicht.
   */
  readonly size?: "sm" | "md";
}

export function EmptyState({ title, children, icon, action, size = "sm" }: EmptyStateProps) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 8,
        padding: size === "md" ? "var(--sp-10) var(--sp-6)" : "var(--sp-7) var(--sp-4)",
      }}
    >
      {icon !== undefined && (
        <span style={{ color: "var(--text-faint)", marginBottom: 2 }}>{icon}</span>
      )}
      <span
        style={{
          fontFamily: "var(--font-display)",
          fontSize: size === "md" ? 17 : 14.5,
          fontWeight: fontWeight.semibold,
          letterSpacing: "var(--tracking-snug)",
          color: "var(--text-body)",
        }}
      >
        {title}
      </span>
      {children !== undefined && (
        <p
          style={{
            margin: 0,
            maxWidth: 420,
            fontSize: 13,
            lineHeight: "var(--lh-normal)",
            color: "var(--text-muted)",
          }}
        >
          {children}
        </p>
      )}
      {action !== undefined && <div style={{ marginTop: 6 }}>{action}</div>}
    </div>
  );
}
